import Grid from '@mui/material/Grid2';
import { Box, Typography } from '@mui/material';
import { ChartComponent } from 'src/view/components/chartComponent';
import ProjectOverview from 'src/resources/images/Project_overview.png';
import EnvPara from 'src/resources/images/envparameter.png';
import CarbonStock from 'src/resources/images/biomass&cstock.png';
import Emission from 'src/resources/images/emission.png';
import { ProjectInfoCard } from 'src/view/components/cardComponent';
import { useAppState } from 'src/state-management/state-provider';
import SiteMapWithLayer from 'src/view/DMap/SiteMapWithLayer';
import SiteList from 'src/view/components/SiteList';
import { isSuccessCarbonAnalysis, roundToTwoDecimalPlaces } from 'src/util/util';
import { Site, UserRole } from 'src/api/Api';
import { CARBON_GLOBAL_LAYERS } from 'src/mapbox-helpers/global-datasets';
import { CustomRasterLayer } from 'src/mapbox-helpers/map-types';
import { useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import { SampleProjectId } from 'src/enum';


const CarbonDashboard = () => {
  const { state } = useAppState();
  const { carbonAnalysis, projectDetails } = state.selectedProject;
  const { user } = state;
  const filterGHGReductionData = carbonAnalysis?.estimated_ghg_removals || {};
  const transformedData = Object.entries(filterGHGReductionData)
    .filter(([key]) => key !== 'unit')
    .map(([key, value]) => ({
      name: key,
      value1: Number(value),
      value2: undefined,
      date: undefined,
    }));

      const isUserRole = useMemo(() => {
      if (!user?.role || user.role.length === 0) return false;
      // Show only if user has ONLY User role (no Admin or Employee)
      const roles = user.role;
      const hasUserRole = roles.includes(UserRole.User);
      const hasAdminRole = roles.includes(UserRole.Admin);
      const hasEmployeeRole = roles.includes(UserRole.Employee);
      // Only show if user has User role and doesn't have Admin or Employee
      return hasUserRole && !hasAdminRole && !hasEmployeeRole;
    }, [user]);
    const isSampleProject = projectDetails?.project_id === SampleProjectId;
    // Disable exporting when project is the sample project AND the current user
    // has only the "User" role. Otherwise allow exporting.
    const allowExporting = !(isSampleProject && isUserRole);
    console.log('Is User Role:', isUserRole, allowExporting);
    // Ensure Highcharts global exporting option follows our allowExporting flag
    useEffect(() => {
      try {
        Highcharts.setOptions({
          exporting: {
            enabled: allowExporting,
          },
        });
      } catch (e) {
        // Ignore if Highcharts isn't ready; this is defensive
        console.warn('Failed to set Highcharts exporting option', e);
      }
    }, [allowExporting]);


  return (
    <Box sx={{ mt: 5 }}>
      {projectDetails?.sites?.length && (
        <Box className="bio-map" mb={3} height={600}>
          <SiteMapWithLayer
            sites={projectDetails?.sites.filter(isSuccessCarbonAnalysis)}
            requiredBounrdaryMarker={true}
            fitToBoundary={true}
            rasterLayer={CARBON_GLOBAL_LAYERS as CustomRasterLayer[]}
          ></SiteMapWithLayer>
        </Box>
      )}
      <Box
        sx={{
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
          marginTop: '5px',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '22px', color: '#151E15', m: 2 }}>
          {projectDetails?.project_name}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #D9D9D9' }}
          gap={1}
        />

        <Grid container spacing={4}>
          <ProjectInfoCard
            imageSrc={ProjectOverview}
            title="Project Details"
            data={[
              {
                label: 'Total Area',
                value: `${roundToTwoDecimalPlaces(projectDetails?.total_area || projectDetails?.sites?.reduce((a: number, i: Site) => a + (i.area ? i.area : 0), 0))} ${projectDetails?.area_unit}`,
              },
              { label: 'Total Sites', value: projectDetails?.sites?.length || 0 },
            ]}
          />

          {carbonAnalysis?.temperature || carbonAnalysis?.precipitation || carbonAnalysis?.soil_type ?
            <ProjectInfoCard
              imageSrc={EnvPara}
              title="Environmental Parameters"
              data={[
                {
                  label: 'Average Temperature',
                  value: `${roundToTwoDecimalPlaces(carbonAnalysis?.temperature)} ${carbonAnalysis?.temperature_unit}`,
                },
                {
                  label: 'Average Precipitation',
                  value: `${roundToTwoDecimalPlaces(carbonAnalysis?.precipitation)} ${carbonAnalysis?.precipitation_unit}`,
                },
                { label: 'Soil Type', value: carbonAnalysis?.soil_type || '' },
              ]}
            /> : <></>}

          {carbonAnalysis?.total_mean_carbon_stock && carbonAnalysis?.total_mean_biomass ? 
          <ProjectInfoCard
            imageSrc={CarbonStock}
            title="Carbon Stock & Biomass"
            data={[
              {
                label: 'Total Mean Biomass',
                value: carbonAnalysis?.total_mean_biomass ? `${roundToTwoDecimalPlaces(carbonAnalysis?.total_mean_biomass)} ${carbonAnalysis?.total_mean_biomass_unit}` : null,
              },
              {
                label: 'Total Mean Carbon Stock',
                value: carbonAnalysis?.total_mean_carbon_stock ? `${roundToTwoDecimalPlaces(carbonAnalysis?.total_mean_carbon_stock)} ${carbonAnalysis?.total_mean_carbon_stock_unit}` : null,
              },
            ]}
          /> : <></>}
          {carbonAnalysis?.estimated_ghg_baseline_emissions || carbonAnalysis?.total_removals || carbonAnalysis?.average_annual_removals ?
            <ProjectInfoCard
              imageSrc={Emission}
              title="Emission & Removals"
              data={[
                {
                  label: 'Estimated GHG Baseline Emissions',
                  value: `${roundToTwoDecimalPlaces(carbonAnalysis?.estimated_ghg_baseline_emissions)} ${carbonAnalysis?.estimated_ghg_baseline_emissions_unit}`,
                },
                {
                  label: 'Total Removals',
                  value: `${roundToTwoDecimalPlaces(carbonAnalysis?.total_removals)} ${carbonAnalysis?.total_removals_unit}`,
                },
                {
                  label: 'Annual Removals',
                  value: `${roundToTwoDecimalPlaces(carbonAnalysis?.average_annual_removals)} ${carbonAnalysis?.average_annual_removals_unit}`,
                },
              ]}
            /> : <></>}
        </Grid>
      </Box>
      {carbonAnalysis?.estimated_ghg_removals && <Grid size={{ lg: 4, md: 6 }} sx={{ marginTop: '10px' }}>
        <ChartComponent
          chartType={'column'}
          chartTitle={'Estimated GHG Emissions reduction/removals'}
          data={transformedData}
          title=" tCO2e"
          seriesValue1=" tCO2e"
          yTitle="Year"
          export={true}
        />
      </Grid>
      }
      <SiteList type={'carbon'}></SiteList>
    </Box>
  );
};

export default CarbonDashboard;
